import { Autocomplete as RHFAutocomplete } from "$components/rhf-fields";
import TextField from "$components/text-field";
import { createFilterOptions } from "@material-ui/lab/Autocomplete";
import { useFormContext, useWatch } from "react-hook-form";
import type { InvoiceItemOption } from "./invoice-view";

const MAX_SUGGESTIONS = 50;
const NO_MATCH_LABEL = "No matching item";

const filterItems = createFilterOptions<InvoiceItemOption>({
  limit: MAX_SUGGESTIONS,
  stringify: (option) => option.label,
});

/**
 * The picker is single-select and not free-solo, but the shared Autocomplete
 * leaves those flags open, so its value type also admits an array and a raw
 * string.
 */
const chosenItemId = (
  option: string | InvoiceItemOption | (string | InvoiceItemOption)[] | null,
): string => {
  const chosen = Array.isArray(option) ? option[0] : option;
  return !chosen || typeof chosen === "string" ? "" : chosen.value;
};

/**
 * A facility price list runs to thousands of entries, so the picker searches
 * instead of listing, and caps what the popup renders.
 *
 * The form holds the catalogue id alone — the option object exists only inside
 * the control — so `value` and `onChange` translate between the two rather than
 * being taken straight off the registered field.
 */
export const PriceListItemField: FC<{
  name: string;
  options: InvoiceItemOption[];
  label?: string;
}> = ({ name, options, label }) => {
  const { setValue } = useFormContext();
  const selectedId = useWatch({ name });
  const selected =
    options.find((option) => option.value === selectedId) ?? null;

  return (
    <RHFAutocomplete<InvoiceItemOption>
      name={name}
      options={options}
      value={selected}
      onChange={(_event, option) =>
        setValue(name, chosenItemId(option), { shouldDirty: true })
      }
      filterOptions={filterItems}
      getOptionLabel={(option) => option.label}
      getOptionSelected={(option, value) => option.value === value.value}
      noOptionsText={NO_MATCH_LABEL}
      fullWidth
      // A line is removed with the bin button, never by blanking its item, and
      // a second adornment would crowd the dense grid's Item column.
      disableClearable
      renderInput={(errStr) => (params) => (
        <TextField
          {...params}
          name={name}
          label={label}
          error={errStr ?? undefined}
          hideHelper
        />
      )}
    />
  );
};
